/**
 * ExportButton — Trigger + modal for PNG export
 *
 * Uses html-to-image (toPng) to capture a ref of TeamImageExport.
 * The preview shown in the modal IS the capture target.
 */
import { useState, useCallback, useRef } from 'react';
import { Download, X, Loader2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import TeamImageExport from '../export/TeamImageExport';
import { exportTeamAsImage } from '../../lib/exportImage';
import { toast } from 'sonner';

export function ExportButton({ team, teamName, teamNotes, disabled = false, renderCustom }) {
  const { language } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  const handleExportImage = useCallback(async () => {
    if (!exportRef.current) return;

    try {
      setExporting(true);
      await exportTeamAsImage(exportRef.current, teamName);
      toast.success(language === 'es' ? 'Imagen exportada' : 'Image exported');
      setShowModal(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'es' ? 'Error al exportar' : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [teamName, language]);

  return (
    <>
      {/* Trigger */}
      {renderCustom ? (
        renderCustom(() => setShowModal(true), disabled)
      ) : (
        <Button
          onClick={() => setShowModal(true)}
          disabled={disabled}
          variant="outline"
          className="gap-2"
        >
          <Download size={16} />
          {language === 'es' ? 'Exportar como imagen' : 'Export as Image'}
        </Button>
      )}

      {/* Modal */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.75)', padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--card-bg,#fff)',
              borderRadius: '1rem', padding: '1.5rem',
              maxWidth: '95vw', maxHeight: '95vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                {language === 'es' ? 'Exportar equipo como imagen' : 'Export Team as Image'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview — scaled down visually, but the ref captures full size */}
            <div style={{
              background: 'rgba(0,0,0,0.02)', borderRadius: '0.5rem',
              padding: '0.5rem', marginBottom: '1rem',
              overflow: 'auto', maxHeight: '70vh',
            }}>
              <div style={{
                transform: 'scale(0.6)',
                transformOrigin: 'top left',
                width: '920px',
              }}>
                <TeamImageExport
                  ref={exportRef}
                  team={team}
                  teamName={teamName}
                  teamNotes={teamNotes}
                  language={language}
                />
              </div>
            </div>

            <p style={{ fontSize: '0.75rem', color: 'var(--muted-fg)', marginBottom: '1rem', textAlign: 'center' }}>
              {language === 'es'
                ? 'Tu equipo será descargado como imagen PNG de alta resolución.'
                : 'Your team will be downloaded as a high resolution PNG image.'}
            </p>

            {/* Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={exporting}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={handleExportImage}
                disabled={exporting}
                style={{
                  backgroundColor: 'var(--primary-color,#7c3aed)',
                  color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                }}
              >
                {exporting ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {language === 'es' ? 'Exportando...' : 'Exporting...'}
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    {language === 'es' ? 'Descargar PNG' : 'Download PNG'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ExportButton;
